import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { CmsOverrideProvider } from "@/lib/cms";
import { DEFAULTS } from "@/lib/cms";
import { previewOgValues } from "@/lib/seoHead";
import MainHeroSection from "@/components/sections/MainHeroSection";
import HeroSection from "@/components/sections/HeroSection";
import AboutSection from "@/components/sections/AboutSection";
import AutomationSection from "@/components/sections/AutomationSection";
import AIPlatformSection from "@/components/sections/AIPlatformSection";
import ContactSection from "@/components/sections/ContactSection";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { CurrentHomePage } from "@/pages/LandingPage";
import { usePreviewModalChrome } from "@/pages/admin/usePreviewModalChrome";
import "./cmsLivePreview.css";

export { usePreviewModalChrome } from "@/pages/admin/usePreviewModalChrome";

const MacbookScrollOpenSection = lazy(
  () => import("@/components/sections/MacbookScrollOpenSection"),
);
const AboutPageAbout = lazy(
  () => import("@/components/sections/company-v2/AboutSection"),
);

function guardPreviewClicks(e) {
  const target = e.target.closest("a, button");
  if (!target) return;
  e.preventDefault();
  e.stopPropagation();
}

export function CmsPreviewFrame({ cms, section, children, testId = "cms-live-preview" }) {
  const viewportRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;
    const update = () => setScale(Math.min(1, el.clientWidth / 1280));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div className={`cms-live-preview cms-live-preview--${section || "full"}`} data-testid={testId}>
      <div className="cms-live-preview__viewport" ref={viewportRef}>
        <div
          className="cms-live-preview__stage"
          style={{ transform: `scale(${scale})` }}
          onClickCapture={guardPreviewClicks}
          onSubmitCapture={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <CmsOverrideProvider value={cms}>
            <div className="ds-landing bg-[#050505] text-white">{children}</div>
          </CmsOverrideProvider>
        </div>
      </div>
    </div>
  );
}

export function CmsSectionPreview({ cms, section }) {
  if (section === "hero") return <MainHeroSection />;
  if (section === "macbook") {
    return (
      <Suspense fallback={<div className="cms-live-preview__loading">MacBook 불러오는 중…</div>}>
        <MacbookScrollOpenSection />
      </Suspense>
    );
  }
  if (section === "about") return <AboutSection />;
  if (section === "sections") {
    return (
      <>
        <AutomationSection />
        <AIPlatformSection />
      </>
    );
  }
  if (section === "contact") return <ContactSection />;
  if (section === "legacy") return <HeroSection showDevicePreview={false} embedMode />;
  if (section === "footer" || section === "basic" || section === "site") {
    // Header is position:fixed. Rendering it here overlays Footer’s circular logo.
    // Site-info preview must match the public Footer: one logo, no Header.
    return <Footer />;
  }
  if (section === "brand") {
    return (
      <>
        <Header embedMode />
        <Footer />
      </>
    );
  }
  if (section === "about-page") {
    return (
      <Suspense fallback={<div className="cms-live-preview__loading">불러오는 중…</div>}>
        <AboutPageAbout />
      </Suspense>
    );
  }
  if (section === "og") {
    const og = previewOgValues(cms || {}, DEFAULTS);
    return <OgShareCard og={og} />;
  }
  return <CurrentHomePage />;
}

export function CmsSplitPreview({
  cms,
  section,
  editor,
  header,
  dirty,
  savedNote,
  testId = "cms-split",
  preview = null,
}) {
  const [pane, setPane] = useState("edit");
  const [fullOpen, setFullOpen] = useState(false);
  const closeFull = useCallback(() => setFullOpen(false), []);

  return (
    <div className={`cms-split cms-split--${pane}`} data-testid={testId}>
      {header}
      <div className="cms-split__toolbar">
        <div className="cms-split__tabs" role="tablist">
          <button type="button" className={pane === "edit" ? "is-on" : ""} onClick={() => setPane("edit")} data-testid="cms-pane-edit">편집</button>
          <button type="button" className={pane === "preview" ? "is-on" : ""} onClick={() => setPane("preview")} data-testid="cms-pane-preview">미리보기</button>
        </div>
        <button type="button" className="cms-split__full-btn" onClick={() => setFullOpen(true)} data-testid="cms-full-preview">
          전체 페이지 미리보기
        </button>
        {dirty ? <span className="cms-split__dirty">저장 전 미리보기</span> : null}
        {savedNote ? <span className="cms-split__saved" data-testid="cms-saved-note">{savedNote}</span> : null}
      </div>
      <div className="cms-split__grid">
        <div className="cms-split__editor">{editor}</div>
        <div className="cms-split__preview">
          {preview || (
            <CmsPreviewFrame cms={cms} section={section}>
              <CmsSectionPreview cms={cms} section={section} />
            </CmsPreviewFrame>
          )}
        </div>
      </div>
      {fullOpen ? (
        <FullPagePreviewModal cms={cms} onClose={closeFull} />
      ) : null}
    </div>
  );
}

export function FullPagePreviewModal({ cms, onClose }) {
  usePreviewModalChrome(onClose);

  const close = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    onClose?.();
  };

  return (
    <div className="cms-full-preview" data-testid="cms-full-preview-modal" role="dialog" aria-modal="true">
      <div className="cms-full-preview__bar">
        <span>전체 페이지 미리보기 · 저장 전 draft · LOCAL/DEV</span>
        <button type="button" onClick={close} aria-label="닫기" className="cms-full-preview__close" data-testid="cms-full-preview-close">
          <X size={16} />
        </button>
      </div>
      <div className="cms-full-preview__body" onClickCapture={guardPreviewClicks}>
        <CmsOverrideProvider value={cms}>
          <div className="ds-landing min-h-screen bg-[#050505] text-white" data-testid="cms-home-preview">
            <CurrentHomePage />
          </div>
        </CmsOverrideProvider>
      </div>
    </div>
  );
}

export function GoogleSerpCard({ title, url, description }) {
  return (
    <div className="seo-serp" data-testid="seo-search-preview">
      <div className="seo-serp__kicker">검색결과 미리보기</div>
      <div className="seo-serp__url">{url || "https://dsitesolution.com/"}</div>
      <div className="seo-serp__title">{title || "제목을 입력하세요"}</div>
      <div className="seo-serp__desc">{description || "설명을 입력하세요"}</div>
    </div>
  );
}

export function OgShareCard({ og }) {
  return (
    <div className="seo-og" data-testid="seo-og-preview">
      <div className="seo-og__kicker">SNS 공유 미리보기</div>
      <div className="seo-og__card">
        {og?.ogImage ? <img src={og.ogImage} alt="" className="seo-og__img" /> : <div className="seo-og__img seo-og__img--empty">이미지 없음</div>}
        <div className="seo-og__body">
          <div className="seo-og__host">{String(og?.ogUrl || "").replace(/^https?:\/\//, "").split("/")[0] || "dsitesolution.com"}</div>
          <div className="seo-og__title">{og?.ogTitle || "제목"}</div>
          <div className="seo-og__desc">{og?.ogDescription || "설명"}</div>
        </div>
      </div>
    </div>
  );
}
